/**
 * Pure helpers for the Study Settings Countries field (StudySetupForm.tsx),
 * kept here so functions' vitest can test them (web has no test runner).
 */
import { isCountryCode, typedCountryCode, typedCountryName } from './protocolCountriesRule';

/**
 * What leaving the "Other countries" box does: the tidied text to show, and
 * the new list to save, or null when the list did not change.
 *
 * D3 (checker, 2026-10-01): the box used to save on every blur, and a save
 * clears the "from the protocol" mark, so merely clicking in and out of the
 * box silently confirmed a prefilled list. The prefilled list can be in any
 * order, so "changed" compares the codes as sets.
 *
 * Typed codes: "UK" is GB. A typed code that has its own checkbox (EU, GB,
 * US, AU, CA) ticks that box instead of being ignored. Any other two letters
 * are saved as typed, even when ISO does not assign them (the form warns,
 * see unrecognisedCountryCodes, but never blocks).
 *
 * Typed NAMES go through the same name normalisation as the protocol path
 * (typedCountryCode): "Japan" is JP, "USA" is US. Semicolons and new lines
 * always separate entries. Commas separate entries too, EXCEPT inside an
 * ISO-style name: within each line the whole text, then each run of adjacent
 * comma pieces, is tried as a name, and the LONGEST match wins, so a piece
 * that only matches after a split never overrides a longer one
 * ("Congo, The Democratic Republic of the" is CD, not "Congo" CG;
 * "Virgin Islands, U.S." is VI, not "U.S." US). A run of two-letter codes
 * separated by spaces ("NZ JP") is split too. An entry that is still not
 * recognised is kept in the box text and returned in `unrecognised`, never
 * dropped.
 */
export function otherCountriesAfterBlur(
  current: string[],
  listed: string[],
  otherText: string,
): { text: string; next: string[] | null; unrecognised: string[] } {
  const codes: string[] = [];
  const unrecognised: string[] = [];
  const add = (c: string) => {
    if (!codes.includes(c)) codes.push(c);
  };
  const keep = (e: string) => {
    if (!unrecognised.some((u) => u.toLowerCase() === e.toLowerCase())) unrecognised.push(e);
  };
  for (const line of otherText.split(/[;\n]+/)) {
    const pieces = line.split(',').map((s) => s.trim()).filter(Boolean);
    let i = 0;
    while (i < pieces.length) {
      const run = longestNameRun(pieces, i, typedCountryName);
      if (run) {
        add(run.code);
        i += run.length;
        continue;
      }
      // One piece: a name, a code, or a run of space-separated codes.
      const p = pieces[i];
      const parts = /^[A-Za-z]{2}(?:\s+[A-Za-z]{2})+$/.test(p) ? p.split(/\s+/) : [p];
      for (const e of parts) {
        const c = typedCountryCode(e);
        if (c) add(c);
        else keep(e);
      }
      i += 1;
    }
  }
  const typed = codes.filter((s) => !listed.includes(s));
  const ticked = current.filter((x) => listed.includes(x));
  const newlyTicked = codes.filter((s) => listed.includes(s) && !ticked.includes(s));
  const next = [...ticked, ...newlyTicked, ...typed];
  const same =
    next.length === current.length && new Set(next).size === new Set(current).size && next.every((c) => current.includes(c));
  return { text: [...typed, ...unrecognised].join(', '), next: same ? null : next, unrecognised };
}

/**
 * The longest run of two or more adjacent comma pieces, starting at `i`,
 * that `lookup` reads as a country name, or null. Longest first, so a
 * shorter match never overrides a longer one.
 */
export function longestNameRun(
  pieces: string[],
  i: number,
  lookup: (name: string) => string,
): { code: string; length: number } | null {
  for (let j = pieces.length; j > i + 1; j--) {
    const code = lookup(pieces.slice(i, j).join(', '));
    if (code) return { code, length: j - i };
  }
  return null;
}

/** Codes in the list that ISO 3166-1 does not assign (EU is the EU/EEA entry). */
export function unrecognisedCountryCodes(list: string[]): string[] {
  return [...new Set(list.filter((c) => !isCountryCode(c)))];
}

/** A plain warning naming codes that are not recognised; '' when there are none. */
export function unrecognisedCodesText(codes: string[]): string {
  if (codes.length === 0) return '';
  if (codes.length === 1) return `${codes[0]} is not a recognised country code. Please check it.`;
  const names = `${codes.slice(0, -1).join(', ')} and ${codes[codes.length - 1]}`;
  return `${names} are not recognised country codes. Please check them.`;
}

/** A plain warning naming typed entries that are not recognised; '' when there are none. */
export function unrecognisedNamesText(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return `${names[0]} is not a recognised country. Please check it, or type its two-letter code.`;
  const list = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${list} are not recognised countries. Please check them, or type their two-letter codes.`;
}

/**
 * The plain-words note for values the protocol gave that are not a single
 * country ("Europe", "Asia-Pacific"). "also" only when something was matched.
 */
export function unmatchedCountriesText(unmapped: string[], anyMatched: boolean): string {
  if (unmapped.length === 0) return '';
  const also = anyMatched ? 'also ' : '';
  const list =
    unmapped.length === 1
      ? unmapped[0]
      : `${unmapped.slice(0, -1).join(', ')} and ${unmapped[unmapped.length - 1]}`;
  const what = unmapped.length === 1 ? 'which is not a single country' : 'which are not single countries';
  return `The protocol ${also}mentions ${list}, ${what}. If the study runs in a specific country there, add its two-letter code below.`;
}
